// @ts-ignore
import { isBetEligible } from '@winamax/missions-lib';
import { createSelector } from 'reselect';

import { getDispatchFromProps, getItemIdOrOddIdFromProps } from '../../../../selectors/_propsGetters_';
import {
  selectBasketIsCashout,
  selectBasketItems,
  selectBasketItemsEntityMatchId,
  selectBasketMode,
  selectBasketState,
  selectBasketStatus,
} from '../../../../selectors/basket/basketPrimary.selector';
import { selectBasketShowResume, makeBasketItemsMatchesSelector } from '../../../../selectors/basketCommon.selector';
import { selectBasketEnhancedItems } from '../../../../selectors/basketItems.selector';
import { selectEntitiesState } from '../../../../selectors/entities/entitiesPrimary.selector';
import { selectUserConnected, selectUserDidReceivedUserData } from '../../../../selectors/user/userPrimary.selector';
import { StateType } from '../../../../states';
import { BasketModes, BasketStatuses } from '../../../../types/basket';
import { isBasketItemsStartWithinEndDate } from '../../../../utils/operations';
import { basketItemSetIsGRActive, basketSetIsGRActive } from '../../actions/basket';
import { GRAssetsBySkinId } from '../../constants/assets';
import { getGRLabelsBySkinId } from '../../constants/labels';
import {
  selectGRGlobalEligibility,
  selectGRGlobalEndDate,
  selectGRGlobalSkinId,
  selectGRGlobalStatus,
} from '../../selectors/global/globalPrimary.selector';
import { selectGRUserCurrentRound, selectGRUserCurrentRoundStatus } from '../../selectors/round.selector';
import { selectGRRoundsState } from '../../selectors/rounds/roundsPrimary.selector';
import { selectIsGREnabledAndNotForbidden } from '../../selectors/runtime.selector';
import { selectGRUserStatus, selectGRUserState, selectGRUserBetslips } from '../../selectors/user/userPrimary.selector';
import GRAdventure from '../../states/user';
import { GRGlobalStatuses } from '../../types/global';
import { GROperationUserStatuses, GRRoundUserStatuses } from '../../types/user';

const selectGRBasketSwitchIsDisabled = createSelector(
  selectBasketStatus,
  (basketStatus) => basketStatus === BasketStatuses.PENDING,
);

const selectIsBasketGREligible = createSelector(
  [
    selectGRGlobalEligibility,
    selectBasketState,
    selectBasketMode,
    getItemIdOrOddIdFromProps,
    selectBasketItems,
    selectEntitiesState,
  ],
  (eligibility, basket, basketMode, oddsId, items, entities) => {
    const isModeSimple = basketMode === BasketModes.SIMPLE;
    const isOneSimpleBet = isModeSimple && Object.keys(items).length === 1;

    if (oddsId) {
      const item = items[oddsId];

      if (item) {
        return isBetEligible({
          eligibility,
          basket,
          item,
          entities,
        });
      }
    }

    if (isOneSimpleBet) {
      const foundOddsId = Object.keys(items)[0];

      if (foundOddsId) {
        const foundItem = items[foundOddsId];

        if (foundItem) {
          return isBetEligible({
            eligibility,
            basket,
            item: foundItem,
            entities,
          });
        }
      }
    } else {
      return isBetEligible({
        eligibility,
        basket,
        entities,
      });
    }
  },
);

const selectHasUserPotentiallyEnoughPoints = createSelector(
  [selectGRUserState, selectGRRoundsState],
  (userState, rounds) => GRAdventure.hasEnoughPoints(userState, rounds),
);

const selectGRBasketSwitchIsVisible = createSelector(
  [
    selectGRUserStatus,
    selectGRUserCurrentRoundStatus,
    selectBasketShowResume,
    getItemIdOrOddIdFromProps,
    selectBasketMode,
    selectBasketEnhancedItems,
    selectUserConnected,
    selectIsBasketGREligible,
    selectIsGREnabledAndNotForbidden,
    selectBasketIsCashout,
    selectGRGlobalStatus,
    selectHasUserPotentiallyEnoughPoints,
  ],
  (
    userStatus,
    userCurrentRoundStatus,
    showResume,
    oddsId,
    basketMode,
    items,
    userIsConnected,
    isBasketGREligible,
    isGREnabled,
    isCashout,
    GRGlobalStatus,
    hasUserPotentiallyEnoughPoints,
  ) => {
    if (
      isGREnabled &&
      GRGlobalStatus === GRGlobalStatuses.RUNNING &&
      userCurrentRoundStatus === GRRoundUserStatuses.OPEN &&
      typeof hasUserPotentiallyEnoughPoints === 'boolean' &&
      !hasUserPotentiallyEnoughPoints
    ) {
      const isModeSimple = basketMode === BasketModes.SIMPLE;
      const isModeCombined = basketMode === BasketModes.COMBINED;
      const isModeSystem = basketMode === BasketModes.SYSTEM;

      const isOneSimpleBet = isModeSimple && items.length === 1;
      const isMultipleSimpleBets = isModeSimple && items.length > 1;

      const isEligible = !(
        isModeSystem ||
        (isModeCombined && !isBasketGREligible) ||
        (isModeSimple && ((isMultipleSimpleBets && !isBasketGREligible) || (isOneSimpleBet && !isBasketGREligible))) ||
        isCashout
      );

      return !!(
        userStatus === GROperationUserStatuses.IN &&
        isEligible &&
        userIsConnected &&
        !showResume &&
        ((isOneSimpleBet && !oddsId) || (!isModeSimple && !oddsId) || (isMultipleSimpleBets && oddsId))
      );
    }

    return false;
  },
);

const selectBasketItemsMatches = makeBasketItemsMatchesSelector();

const selectHasCurrentRoundAnyPendingBetslipWithSameMatchId = createSelector(
  [
    selectUserDidReceivedUserData,
    selectBasketItemsEntityMatchId,
    selectGRUserCurrentRound,
    selectGRUserBetslips,
    selectBasketMode,
    selectBasketItems,
  ],
  (didUserReceivedData, basketItemMatchId, currentRound, userBetslips, basketMode, basketItems) => {
    if (didUserReceivedData) {
      const isModeSimple = basketMode === BasketModes.SIMPLE;
      const isModeCombined = basketMode === BasketModes.COMBINED;
      const isOneSimpleBet = isModeSimple && Object.keys(basketItems).length === 1;

      if (isOneSimpleBet) {
        const foundOddsId = Object.keys(basketItems)[0];
        const foundedBasketItemMatchId = basketItems[foundOddsId]?.matchId;

        if (foundedBasketItemMatchId) {
          return GRAdventure.hasRoundAnyPendingBetslipWithSameMatchId({
            round: currentRound,
            matchId: foundedBasketItemMatchId,
            userBetslips,
          });
        }

        return false;
      }

      if (isModeSimple && basketItemMatchId) {
        return GRAdventure.hasRoundAnyPendingBetslipWithSameMatchId({
          round: currentRound,
          matchId: basketItemMatchId,
          userBetslips,
        });
      }

      if (isModeCombined) {
        const itemIds = Object.keys(basketItems);

        return itemIds.some((itemId) => {
          const { matchId: currBasketItemMatchId } = basketItems[itemId];

          return GRAdventure.hasRoundAnyPendingBetslipWithSameMatchId({
            round: currentRound,
            matchId: currBasketItemMatchId,
            userBetslips,
          });
        });
      }
    }

    return false;
  },
);

const selectIsAutoToggleOnRender = createSelector(
  [
    selectBasketMode,
    getItemIdOrOddIdFromProps,
    selectBasketItemsEntityMatchId,
    selectBasketItemsMatches,
    selectGRGlobalEndDate,
    selectHasUserPotentiallyEnoughPoints,
    selectHasCurrentRoundAnyPendingBetslipWithSameMatchId,
  ],
  (
    basketMode,
    oddsId,
    basketItem,
    basketItemsMatches,
    grEndDate,
    hasEnoughPoints,
    hasCurrentRoundAnyPendingBetslipWithSameMatchId,
  ) => {
    if (hasEnoughPoints) {
      return false;
    }

    if (hasCurrentRoundAnyPendingBetslipWithSameMatchId) {
      return false;
    }

    return isBasketItemsStartWithinEndDate(basketMode, basketItemsMatches, grEndDate, basketItem);
  },
);

const selectGRBasketSwitchIsToggled = createSelector(
  [selectBasketMode, getItemIdOrOddIdFromProps, selectBasketItems, selectBasketState],
  (basketMode, oddsId, basketItems, basket) => {
    const isModeSimple = basketMode === BasketModes.SIMPLE;
    const isModeCombined = basketMode === BasketModes.COMBINED;
    const isOneSimpleBet = isModeSimple && Object.keys(basketItems).length === 1;

    if (isOneSimpleBet) {
      const foundOddsId = Object.keys(basketItems)[0];

      return !!basketItems[foundOddsId]?.isGRActive;
    }

    if (isModeSimple && oddsId) {
      return !!basketItems[oddsId]?.isGRActive;
    }

    if (isModeCombined) {
      return !!basket.isGRActive;
    }

    return false;
  },
);

const selectGRBasketSwitchOnToggle = createSelector(
  [getDispatchFromProps, getItemIdOrOddIdFromProps, selectBasketMode, selectBasketItems],
  (dispatch, oddsId, basketMode, basketItems) => (isSelected: boolean) => {
    const isModeSimple = basketMode === BasketModes.SIMPLE;
    const isOneSimpleBet = isModeSimple && Object.keys(basketItems).length === 1;

    if (dispatch) {
      if (oddsId) {
        dispatch(
          basketItemSetIsGRActive({
            oddsId,
            isGRActive: isSelected ? !!isSelected : false,
          }),
        );
      } else {
        if (isOneSimpleBet) {
          const foundOddsId = Object.keys(basketItems)[0];

          if (foundOddsId) {
            dispatch(
              basketItemSetIsGRActive({
                oddsId: foundOddsId,
                isGRActive: isSelected ? !!isSelected : false,
              }),
            );
          }
        } else {
          dispatch(
            basketSetIsGRActive({
              isGRActive: isSelected ? !!isSelected : false,
            }),
          );
        }
      }
    }
  },
);

const selectGRBasketSwitchLabel = (state: StateType) => {
  const skinId = selectGRGlobalSkinId(state);

  if (!skinId) {
    return '';
  }

  return getGRLabelsBySkinId(skinId)?.OperationTitle;
};

const selectGRBasketSwitchIcon = (state: StateType) => {
  const skinId = selectGRGlobalSkinId(state);

  if (!skinId) {
    return '';
  }

  return GRAssetsBySkinId[skinId]?.IconPath;
};

const selectGRBasketSwitchProps = createSelector(
  [
    selectGRBasketSwitchIsVisible,
    selectGRBasketSwitchIsDisabled,
    selectIsAutoToggleOnRender,
    selectGRBasketSwitchIsToggled,
    selectGRBasketSwitchOnToggle,
    selectBasketMode,
    selectGRBasketSwitchLabel,
    selectGRBasketSwitchIcon,
  ],
  (isVisible, disabled, autoToggleOnRender, toggled, onToggle, basketMode, label, icon) => ({
    isVisible,
    disabled,
    autoToggleOnRender,
    toggled,
    onToggle,
    basketMode,
    label,
    icon,
  }),
);

export default selectGRBasketSwitchProps;
